import React, { useState } from 'react';
import Button from '../../NecttosComp/Button/Button';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import toast from 'react-hot-toast';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { message } from 'antd';
import Input2 from '../../NecttosComp/Input/Input2';

interface CollegeDetailsViewProps {
	college: any;
	onBack: () => void;
	refetchData: () => void;
}

const CollegeDetailsView: React.FC<CollegeDetailsViewProps> = ({ college, onBack, refetchData }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [formData, setFormData] = useState({ ...college });
	const [selectedFile, setNewLogoFile] = useState<File | null>(null);
	const [loading, setLoading] = useState(false);
	const storage = getStorage();

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { type, files } = e.target;
		if (type === 'file' && files && files[0]) {
			setNewLogoFile(files[0]);
		} else {
			setFormData({ ...formData, [e.target.name]: e.target.value });
		}
	};

	const saveChanges = async () => {
		setLoading(true);
		try {
			const instance = await getAxiosTokenInstance();

			const payload = {
				...formData,
				collegeId: college._id,
				institutionType: formData.newInstitutionType,
			};
			delete payload.newInstitutionType;

			if (selectedFile) {
				const fileBlob = selectedFile instanceof Blob ? selectedFile : new Blob([selectedFile]);

				try {
					const logoImagesRef = ref(storage, `collegeLogo/${college._id}/collegeLogo.png`);

					const snapshot = await uploadBytes(logoImagesRef, fileBlob, {
						contentType: 'image/png',
					});

					const url = await getDownloadURL(snapshot.ref);

					payload.logo = url;

					message.success('Photo uploaded successfully.');
				} catch (error) {
					console.error('Error on uploading', error);
					message.error('API Error');
				}
			}

			

			const response = await instance.post('/register/updateCollege', payload);

			toast.success('College details updated successfully!');
			setFormData(response.data.updatedCollege || formData);
			setIsEditing(false);
			refetchData();
		} catch (err: any) {
			console.error(err);
			toast.error(err?.response?.data?.message || 'Failed to update details');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='p-8 bg-white rounded-2xl shadow-xl max-w-4xl mx-auto'>
			{formData.logo && (
				<div className='flex justify-center mb-6'>
					<img src={formData.logo} alt='College Logo' className='w-32 h-32 object-contain rounded-full border-4 border-gray-200 shadow' />
				</div>
			)}

			<div className='text-center mb-6'>{isEditing ? <input type='text' name='collegeName' value={formData.collegeName || ''} onChange={handleInputChange} className='text-3xl font-bold text-blue-800 text-center w-full p-2 border-b border-blue-300 focus:outline-none' placeholder='Enter College Name' /> : <h1 className='text-3xl font-bold text-blue-800'>{formData.collegeName || 'College Details'}</h1>}</div>

			<div className='grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm text-gray-700'>
				{fields.map((field) => {
					let { label, name, type } = field;

					if(name === 'newInstitutionType' && !isEditing){
						name = 'institutionType';
					}
					return (
						<div key={name} className='flex flex-col gap-1'>
							<label className='font-medium'>{label}</label>

							{isEditing ? (
								<div className='w-[400px]'>
									<Input2 width='400px' className='w-[400px]' inputValue={name} returnKey={field.name} fieldName={name} type={type} options={field.options} state={formData} setState={setFormData} />
								</div>
							) : type === 'file' ? null : (
								<p className='text-gray-800'>{Array.isArray(formData[name]) ? formData[name].join(', ') : typeof formData[name] === 'object' ? JSON.stringify(formData[name]) : formData[name] || '-'}</p>
							)}
						</div>
					);
				})}
			</div>

			<div className='mt-8 text-center flex justify-center gap-4'>
				<Button type='close' onClick={onBack}>
					Back to List
				</Button>
				{isEditing ? (
					<Button type='submit' onClick={saveChanges} disabled={loading}>
						{loading ? 'Saving...' : 'Save'}
					</Button>
				) : (
					<Button type='edit' onClick={() => setIsEditing(true)}>
						Edit / Add Details
					</Button>
				)}
			</div>
		</div>
	);
};

const fields = [
	{ label: 'Short Name', name: 'shortName', type: 'text' },
	{ label: 'Slogan', name: 'slogan', type: 'text' },
	{ label: 'Established Year', name: 'establishedYear', type: 'text' },
	{ label: 'Founder', name: 'founder', type: 'text' },
	{ label: 'Principal Name', name: 'principalName', type: 'text' },
	{ label: 'Accreditation', name: 'accreditation', type: 'text' },
	{ label: 'University', name: 'university', type: 'text' },
	{ label: 'Email', name: 'email', type: 'text' },
	{ label: 'Phone Number', name: 'phoneNumber', type: 'text' },
	{ label: 'Website', name: 'website', type: 'text' },
	{ label: 'Address', name: 'address', type: 'text' },
	{ label: 'District', name: 'district', type: 'text' },
	{ label: 'Pincode', name: 'pincode', type: 'text' },
	{ label: 'Area', name: 'area', type: 'text' },
	{
		label: 'Campus Type',
		name: 'campusType',
		type: 'drop',
		options: ['Urban', 'Rural'],
	},
	{
		label: 'Organization',
		name: 'organization',
		type: 'drop',
		options: ['Government', 'Trust', 'Co-operate'],
	},
	{
		label: 'Institution Type',
		name: 'newInstitutionType',
		type: 'mdrop',
		options: ['aided', 'self', 'govt'],
	},
	{
		label: 'Affiliated Type',
		name: 'affiliatedType',
		type: 'drop',
		options: ['Affiliated', 'Autonomous', 'University'],
	},
	{ label: 'Gender Type', name: 'genderType', type: 'drop', options: ['Mixed', 'Boys', 'Girls'] },
	{
		label: 'Education Levels',
		name: 'educationLevels',
		type: 'mdrop',
		options: ['kinder Garden', 'Under Graduation', 'Lower Primary', 'Post Graduation', 'Upper Primary', 'Doctor of Philosophy', 'High School', 'Diploma', 'Higher Secondary Education', 'Teacher Education'],
	},
	{
		label: 'Education Category',
		name: 'educationCategory',
		type: 'mdrop',
		options: ['Arts and Science', 'Law', 'Engineering', 'Commerce', 'Polytechnic', 'ITI', 'Medical'],
	},
	{ label: 'Upload Logo *', name: 'logo', type: 'file' },
];

export default CollegeDetailsView;
